import { displayDate } from "@/lib/dates";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { RegisterFeedback, PageHeader, EmptyState } from "./_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Archive, ArchiveRestore, Search, MapPin, ArrowRight, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";

function money(n) {
  return `$${(n || 0).toLocaleString("en-NZ", { maximumFractionDigits: 0 })}`;
}

export default function ArchivedJobs() {
  const { user } = useAuth();
  const isOffice = user.role !== "field" && user.role !== "mechanic";
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const load = async (query = "") => {
    setLoadError(false);
    setLoaded(false);
    try {
      const { data } = await api.get("/jobs/archived", { params: query ? { q: query } : {} });
      setItems(data);
    } catch (error) {
      setLoadError(true);
      console.warn("Archived jobs request failed", { status: error.response?.status });
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Debounced server-side search
  useEffect(() => {
    const t = setTimeout(() => load(q.trim()), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const restore = async (id, ev) => {
    ev?.stopPropagation?.();
    if (!window.confirm("Restore this job? It will reappear across the active job screens.")) return;
    try {
      await api.post(`/jobs/${id}/unarchive`);
      setItems((list) => list.filter((j) => j.id !== id));
      toast.success("Job restored");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to restore"); }
  };

  if (!isOffice) {
    return (
      <div className="px-4 py-6 md:p-6" data-testid="archived-jobs-no-access">
        <EmptyState title="Archived Jobs" description="Only office and admin users can view archived jobs." />
      </div>
    );
  }

  return (
    <div data-testid="archived-jobs-page" className="px-4 py-4 md:p-6 lg:p-6 w-full pb-24 lg:pb-10">
      <PageHeader
        tag="/ Completed & filed"
        title="Archived Jobs"
        subtitle="Finished jobs you've filed away. Search the archive and restore any job back to active work."
      />

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="archived-toolbar">
        <div className="relative flex-1 min-w-[180px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Search archived jobs"
            placeholder="Search by job, number, client or address…"
            className="pl-9 w-full rounded-lg border-zinc-300 dark:border-white/15 bg-white dark:bg-[#101620]"
            data-testid="archived-search-input"
          />
        </div>
        <span className="text-xs font-mono uppercase tracking-widest text-zinc-400 ml-auto" data-testid="archived-count">
          {items.length} archived
        </span>
      </div>

      {loadError ? <RegisterFeedback error onRetry={() => load(q.trim())} label="archived jobs" /> : !loaded ? (
        <div className="space-y-2" data-testid="archived-loading">
          {[0, 1, 2, 3].map((i) => (<div key={i} className="h-16 rounded-xl border border-zinc-200 dark:border-white/10 bg-zinc-50 dark:bg-white/5 animate-pulse" />))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          title={q.trim() ? "No archived jobs match" : "No archived jobs yet"}
          description={q.trim()
            ? "Try a different search term."
            : "When you finish a job, mark it Completed and choose Archive from the job menu to file it here."}
        />
      ) : (
        <div className="space-y-1.5" data-testid="archived-jobs-list">
          {items.map((j) => (
            <div
              key={j.id}
              onClick={(event) => {
                if (event.defaultPrevented || event.target.closest('a, button')) return;
                navigate(`/app/jobs/${encodeURIComponent(j.id)}`);
              }}
              className="group relative flex items-center gap-4 px-4 py-3 rounded-lg border border-zinc-200 dark:border-white/10 bg-white dark:bg-[#101620] hover:bg-zinc-50 dark:hover:bg-[#151e2b] hover:border-zinc-300 dark:hover:border-slate-600 transition-colors cursor-pointer"
              data-testid={`archived-row-${j.id}`}
            >
              <Archive className="w-4 h-4 text-zinc-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 min-w-0">
                  {j.job_number && (
                    <span className="font-mono font-bold text-[10px] tracking-widest text-[#FF5F15] shrink-0">{j.job_number}</span>
                  )}
                  <Link
                    to={`/app/jobs/${encodeURIComponent(j.id)}`}
                    title={j.title}
                    className="block font-semibold text-[14px] leading-snug line-clamp-1 rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FF5F15]"
                    data-testid={`archived-title-${j.id}`}
                  >
                    {j.title}
                  </Link>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 text-[11.5px] text-zinc-500 dark:text-zinc-400 mt-0.5">
                  {j.client_name && <span className="truncate max-w-[220px]">{j.client_name}</span>}
                  {j.site_address && <span className="inline-flex items-center gap-1 min-w-0"><MapPin className="w-3 h-3 shrink-0" /><span className="truncate max-w-[220px]">{j.site_address}</span></span>}
                  <span className="inline-flex items-center gap-1"><UsersIcon className="w-3 h-3" />{j.assigned_user_ids?.length || 0}</span>
                  {j.quoted_value ? <span className="font-bold tabular-nums text-zinc-600 dark:text-zinc-300">{money(j.quoted_value)}</span> : null}
                </div>
              </div>
              <div className="hidden sm:block text-[11px] font-mono text-zinc-400 whitespace-nowrap shrink-0" title="Archived on">
                {j.archived_at ? displayDate(j.archived_at) : "—"}
              </div>
              <Button
                size="sm"
                variant="outline"
                className="h-9 rounded-lg text-[11px] font-bold uppercase tracking-wide shrink-0"
                onClick={(e) => restore(j.id, e)}
                data-testid={`archived-restore-${j.id}`}
              >
                <ArchiveRestore className="w-3.5 h-3.5 mr-1.5" /> Restore
              </Button>
              <ArrowRight className="w-4 h-4 text-[#FF5F15] opacity-0 group-hover:opacity-100 transition-opacity hidden md:block shrink-0" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
